import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type {
  MemberTagDefinitionDto,
  MemberTagRuleDto,
  MemberTagRuleInput,
  MemberTagRunDto,
  TagCoverage,
  TagNativeOrigin,
} from '@pokerroom/types';
import { api } from '../../lib/api';
import { useAuthStore } from '../../store/auth.store';
const control = 'border border-base-300 rounded px-3 py-2 bg-base-100 max-w-full';
const instant = (value: string) => new Date(value).toISOString();
const local = (value?: string | null) =>
  value
    ? new Date(new Date(value).getTime() - new Date(value).getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16)
    : '';
type List = {
  data: MemberTagRuleDto[];
  total: number;
  runtimeEnabled: boolean;
  currentVenueId: string;
  venues: { id: string; name: string }[];
};
export function MemberTagRules({ tags }: { tags: MemberTagDefinitionDto[] }) {
  const { t } = useTranslation('member'),
    qc = useQueryClient();
  const actions = useAuthStore((s) => s.allowedActions),
    session = useAuthStore((s) => s.sessionRevision);
  const manage = actions?.includes('member:tag_rules_manage'),
    run = actions?.includes('member:tag_rules_run'),
    global = actions?.includes('member:tags_assign_global');
  const [open, setOpen] = useState(false),
    [page, setPage] = useState(1),
    [error, setError] = useState(''),
    [busy, setBusy] = useState(false);
  const [edit, setEdit] = useState<MemberTagRuleDto | null>(null),
    [form, setForm] = useState<MemberTagRuleInput | null>(null),
    [effective, setEffective] = useState('');
  const [report, setReport] = useState<MemberTagRunDto | null>(null),
    [audit, setAudit] = useState<string | null>(null),
    [auditPage, setAuditPage] = useState(1);
  const list = useQuery({
    queryKey: ['member-tag-rules', session, page],
    enabled: open,
    queryFn: () => api.get<List>(`/member-tag-rules?page=${page}`),
  });
  const history = useQuery({
    queryKey: ['member-tag-rule-history', session, audit, auditPage],
    enabled: !!audit,
    queryFn: () =>
      api.get<{ data: MemberTagRunDto[]; total: number }>(
        `/member-tag-rules/${audit}/runs?page=${auditPage}`,
      ),
  });
  const refresh = () => {
    for (const key of [
      'member-tag-rules',
      'member-tag-rule-history',
      'member-tags-detail',
      'members',
    ])
      void qc.invalidateQueries({ queryKey: [key] });
  };
  const perform = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      refresh();
    } catch (e) {
      const code = (e as { response?: { data?: { code?: string } } }).response?.data?.code;
      setError(t(`tagRules.errors.${code ?? 'FAILED'}`, { defaultValue: t('tagRules.failed') }));
    } finally {
      setBusy(false);
    }
  };
  const change = (patch: Partial<MemberTagRuleInput>) =>
    setForm((f) => (f ? { ...f, ...patch } : f));
  const params = (patch: Partial<MemberTagRuleInput['parameters']>) =>
    setForm((f) => (f ? { ...f, parameters: { ...f.parameters, ...patch } } : f));
  const coverage = (index: number, patch: Partial<TagCoverage>) =>
    params({
      coverage: form!.parameters.coverage.map((c, i) => (i === index ? { ...c, ...patch } : c)),
    });
  const native = (index: number, patch: Partial<TagNativeOrigin>) =>
    params({
      nativeOrigins: form!.parameters.nativeOrigins!.map((c, i) =>
        i === index ? { ...c, ...patch } : c,
      ),
    });
  const begin = (rule?: MemberTagRuleDto) => {
    setEdit(rule ?? null);
    setError('');
    setForm(
      rule
        ? structuredClone({
            tagId: rule.tagId,
            scope: rule.scope,
            kind: rule.kind,
            parameters: rule.parameters,
          })
        : {
            tagId: tags.find((t) => !t.archivedAt)?.id ?? '',
            scope: 'VENUE',
            kind: 'TOURNAMENT_PARTICIPATION',
            parameters: {
              venueIds: [list.data!.currentVenueId],
              windowFrom: new Date().toISOString(),
              windowUntil: new Date(Date.now() + 86400000).toISOString(),
              coverage: [],
            },
          },
    );
  };
  const more = (current: number, total: number, update: (p: number) => void) =>
    total > 100 && (
      <div>
        <button
          className={control}
          disabled={current === 1 || busy}
          onClick={() => update(current - 1)}
        >
          {t('tags.previous')}
        </button>
        <button
          className={control}
          disabled={current * 100 >= total || busy}
          onClick={() => update(current + 1)}
        >
          {t('tags.next')}
        </button>
      </div>
    );
  return (
    <div className="space-y-3" aria-label={t('tagRules.title')}>
      <button className={control} onClick={() => setOpen(!open)} aria-expanded={open}>
        {t('tagRules.title')}
      </button>
      {open && (
        <>
          <p>{t('tagRules.schedule')}</p>
          <p>{t('tagRules.coverageHint')}</p>
          {list.isLoading && <p role="status">{t('tagRules.loading')}</p>}
          {list.isError && <p role="alert">{t('tagRules.failed')}</p>}
          {list.data && !list.data.runtimeEnabled && (
            <p role="status">{t('tagRules.runtimeDisabled')}</p>
          )}
          {error && <p role="alert">{error}</p>}
          {manage && list.data && (
            <button className={control} onClick={() => begin()} disabled={busy}>
              {t('tagRules.create')}
            </button>
          )}
          <ul className="space-y-3">
            {list.data?.data.map((rule) => (
              <li key={rule.id} className="border rounded p-3 space-y-2">
                <p>
                  {rule.definition.name} · {t(`tagRules.${rule.kind}`)} · {t(`tags.${rule.scope}`)}{' '}
                  · {t(`tagRules.${rule.status}`)} ·{' '}
                  {t('tagRules.revision', { value: rule.revisionNumber })}
                </p>
                {rule.lastRun && (
                  <p>
                    {t('tagRules.computed', {
                      date: new Date(rule.lastRun.cutoffInstant).toLocaleString(),
                    })}{' '}
                    · {t(`tagRules.${rule.lastRun.status}`)}
                    {(rule.lastRun.status !== 'SUCCEEDED' ||
                      Date.now() - Date.parse(rule.lastRun.cutoffInstant) > 36 * 3600000) &&
                      ` · ${t('tagRules.stale')}`}
                  </p>
                )}
                <div className="flex flex-wrap gap-2">
                  {manage && ['DRAFT', 'PAUSED'].includes(rule.status) && (
                    <button className={control} onClick={() => begin(rule)} disabled={busy}>
                      {t('tags.edit')}
                    </button>
                  )}
                  {run && (
                    <button
                      className={control}
                      disabled={busy}
                      onClick={() =>
                        void perform(async () =>
                          setReport(
                            await api.post<MemberTagRunDto>(
                              `/member-tag-rules/${rule.id}/preview`,
                              { operationKey: crypto.randomUUID() },
                            ),
                          ),
                        )
                      }
                    >
                      {t('tagRules.preview')}
                    </button>
                  )}
                  {run && rule.status === 'ACTIVE' && (
                    <button
                      className={control}
                      disabled={
                        busy ||
                        !list.data?.runtimeEnabled ||
                        !rule.effectiveAt ||
                        Date.parse(rule.effectiveAt) > Date.now()
                      }
                      onClick={() =>
                        void perform(async () =>
                          setReport(
                            await api.post<MemberTagRunDto>(`/member-tag-rules/${rule.id}/run`, {
                              operationKey: crypto.randomUUID(),
                            }),
                          ),
                        )
                      }
                    >
                      {t('tagRules.run')}
                    </button>
                  )}
                  {manage && rule.status === 'ACTIVE' && (
                    <button
                      className={control}
                      disabled={busy}
                      onClick={() =>
                        void perform(() =>
                          api.post(`/member-tag-rules/${rule.id}/pause`, {
                            expectedVersion: rule.version,
                          }),
                        )
                      }
                    >
                      {t('tagRules.pause')}
                    </button>
                  )}
                  {manage && ['DRAFT', 'PAUSED'].includes(rule.status) && (
                    <label>
                      {t('tagRules.effectiveAt')}
                      <input
                        aria-label={`${t('tagRules.effectiveAt')} ${rule.id}`}
                        className={control}
                        type="datetime-local"
                        value={effective}
                        onChange={(e) => setEffective(e.target.value)}
                      />
                      <button
                        className={control}
                        disabled={
                          busy ||
                          !effective ||
                          !list.data?.runtimeEnabled ||
                          rule.definition.archivedAt !== null
                        }
                        onClick={() =>
                          void perform(() =>
                            api.post(`/member-tag-rules/${rule.id}/activate`, {
                              expectedVersion: rule.version,
                              effectiveAt: instant(effective),
                            }),
                          )
                        }
                      >
                        {t('tagRules.activate')}
                      </button>
                    </label>
                  )}
                  <button
                    className={control}
                    onClick={() => {
                      setAudit(rule.id);
                      setAuditPage(1);
                    }}
                  >
                    {t('tagRules.audit')}
                  </button>
                </div>
              </li>
            ))}
          </ul>
          {more(page, list.data?.total ?? 0, setPage)}
          {form && manage && (
            <form
              className="border rounded p-3 space-y-3 [&_label]:flex [&_label]:flex-col [&_label]:gap-1"
              aria-label={t('tagRules.editor')}
              onSubmit={(e) => {
                e.preventDefault();
                void perform(async () => {
                  if (edit)
                    await api.patch(`/member-tag-rules/${edit.id}`, {
                      expectedVersion: edit.version,
                      parameters: form.parameters,
                    });
                  else await api.post('/member-tag-rules', form);
                  setForm(null);
                });
              }}
            >
              <label>
                {t('tagRules.tag')}
                <select
                  className={control}
                  required
                  disabled={!!edit}
                  value={form.tagId}
                  onChange={(e) => change({ tagId: e.target.value })}
                >
                  <option value="">{t('tagRules.chooseTag')}</option>
                  {tags
                    .filter((t) => !t.archivedAt)
                    .map((tag) => (
                      <option key={tag.id} value={tag.id}>
                        {tag.name}
                      </option>
                    ))}
                </select>
              </label>
              <label>
                {t('tagRules.kind')}
                <select
                  className={control}
                  disabled={!!edit}
                  value={form.kind}
                  onChange={(e) => {
                    const kind = e.target.value as MemberTagRuleInput['kind'];
                    change({
                      kind,
                      parameters: {
                        venueIds: form.parameters.venueIds,
                        coverage: [],
                        ...(kind === 'TOURNAMENT_PARTICIPATION'
                          ? {
                              windowFrom: new Date().toISOString(),
                              windowUntil: new Date(Date.now() + 86400000).toISOString(),
                            }
                          : {}),
                      },
                    });
                  }}
                >
                  {['TOURNAMENT_PARTICIPATION', 'INACTIVE'].map((k) => (
                    <option key={k} value={k}>
                      {t(`tagRules.${k}`)}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                {t('tagRules.scope')}
                <select
                  className={control}
                  disabled={!!edit}
                  value={form.scope}
                  onChange={(e) =>
                    change({
                      scope: e.target.value as MemberTagRuleInput['scope'],
                      parameters: {
                        ...form.parameters,
                        venueIds: [list.data!.currentVenueId],
                        coverage: [],
                      },
                    })
                  }
                >
                  <option value="VENUE">{t('tags.VENUE')}</option>
                  {global && <option value="ASSOCIATION">{t('tags.ASSOCIATION')}</option>}
                </select>
              </label>
              <label>
                {t('tagRules.venues')}
                <select
                  className={control}
                  multiple
                  required
                  disabled={form.scope === 'VENUE'}
                  value={form.parameters.venueIds}
                  onChange={(e) =>
                    params({
                      venueIds: Array.from(e.target.selectedOptions, (o) => o.value),
                      coverage: [],
                    })
                  }
                >
                  {list.data?.venues.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name}
                    </option>
                  ))}
                </select>
              </label>
              {form.kind === 'TOURNAMENT_PARTICIPATION' ? (
                <div className="flex flex-wrap gap-2">
                  {(['windowFrom', 'windowUntil'] as const).map((key) => (
                    <label key={key}>
                      {t(`tagRules.${key}`)}
                      <input
                        className={control}
                        type="datetime-local"
                        required
                        value={local(form.parameters[key])}
                        onChange={(e) =>
                          params({ [key]: e.target.value ? instant(e.target.value) : '' })
                        }
                      />
                    </label>
                  ))}
                  {(['minBuyin', 'maxBuyin'] as const).map((key) => (
                    <label key={key}>
                      {t(`tagRules.${key}`)}
                      <input
                        className={control}
                        type="number"
                        min="0"
                        step="0.01"
                        value={form.parameters[key] ?? ''}
                        onChange={(e) => params({ [key]: e.target.value || undefined })}
                      />
                    </label>
                  ))}
                </div>
              ) : (
                <>
                  <p>{t('tagRules.inactiveHint')}</p>
                  {form.parameters.venueIds.map((venueId) => {
                    const index = form.parameters.coverage.findIndex((c) => c.venueId === venueId),
                      c = form.parameters.coverage[index];
                    return (
                      <fieldset className="border p-2 space-y-2" key={venueId}>
                        <legend>{list.data?.venues.find((v) => v.id === venueId)?.name}</legend>
                        {!c ? (
                          <button
                            type="button"
                            className={control}
                            onClick={() =>
                              params({
                                coverage: [...form.parameters.coverage, { venueId, gaps: [] }],
                              })
                            }
                          >
                            {t('tagRules.addCoverage')}
                          </button>
                        ) : (
                          <>
                            <label>
                              {t('tagRules.observationStart')}
                              <input
                                className={control}
                                type="datetime-local"
                                required
                                value={local(c.observationStart)}
                                onChange={(e) =>
                                  coverage(index, {
                                    observationStart: e.target.value
                                      ? instant(e.target.value)
                                      : undefined,
                                  })
                                }
                              />
                            </label>
                            <label>
                              {t('tagRules.evidenceRef')}
                              <input
                                className={control}
                                required
                                maxLength={128}
                                value={c.evidenceRef ?? ''}
                                onChange={(e) => coverage(index, { evidenceRef: e.target.value })}
                              />
                            </label>
                            <label>
                              {t('tagRules.evidenceSha256')}
                              <input
                                className={control}
                                required
                                pattern="[a-fA-F0-9]{64}"
                                value={c.evidenceSha256 ?? ''}
                                onChange={(e) =>
                                  coverage(index, { evidenceSha256: e.target.value })
                                }
                              />
                            </label>
                            {c.gaps.map((gap, g) => (
                              <div key={g}>
                                {(['from', 'until'] as const).map((key) => (
                                  <label key={key}>
                                    {t(`tagRules.${key}`)}
                                    <input
                                      className={control}
                                      type="datetime-local"
                                      required
                                      value={local(gap[key])}
                                      onChange={(e) =>
                                        coverage(index, {
                                          gaps: c.gaps.map((v, i) =>
                                            i === g
                                              ? {
                                                  ...v,
                                                  [key]: e.target.value
                                                    ? instant(e.target.value)
                                                    : '',
                                                }
                                              : v,
                                          ),
                                        })
                                      }
                                    />
                                  </label>
                                ))}
                                <button
                                  type="button"
                                  className={control}
                                  onClick={() =>
                                    coverage(index, { gaps: c.gaps.filter((_, i) => i !== g) })
                                  }
                                >
                                  {t('tags.remove')}
                                </button>
                              </div>
                            ))}
                            <button
                              type="button"
                              className={control}
                              onClick={() =>
                                coverage(index, {
                                  gaps: [
                                    ...c.gaps,
                                    {
                                      from: new Date().toISOString(),
                                      until: new Date().toISOString(),
                                    },
                                  ],
                                })
                              }
                              disabled={c.gaps.length >= 100}
                            >
                              {t('tagRules.addGap')}
                            </button>
                            <button
                              type="button"
                              className={control}
                              onClick={() =>
                                params({
                                  coverage: form.parameters.coverage.filter((_, i) => i !== index),
                                })
                              }
                            >
                              {t('tags.remove')}
                            </button>
                          </>
                        )}
                      </fieldset>
                    );
                  })}
                  <p>{t('tagRules.nativeHint')}</p>
                  {form.parameters.nativeOrigins?.map((n, index) => (
                    <fieldset className="border p-2" key={index}>
                      {(['memberId', 'evidenceRef', 'evidenceSha256'] as const).map((key) => (
                        <label key={key}>
                          {t(`tagRules.${key}`)}
                          <input
                            className={control}
                            required
                            maxLength={
                              key === 'memberId' ? 36 : key === 'evidenceSha256' ? 64 : 128
                            }
                            value={n[key]}
                            onChange={(e) => native(index, { [key]: e.target.value })}
                          />
                        </label>
                      ))}
                      <label>
                        {t('tagRules.observationStart')}
                        <input
                          className={control}
                          required
                          type="datetime-local"
                          value={local(n.observedAt)}
                          onChange={(e) =>
                            native(index, {
                              observedAt: e.target.value ? instant(e.target.value) : '',
                            })
                          }
                        />
                      </label>
                      <button
                        type="button"
                        className={control}
                        onClick={() =>
                          params({
                            nativeOrigins: form.parameters.nativeOrigins!.filter(
                              (_, i) => i !== index,
                            ),
                          })
                        }
                      >
                        {t('tags.remove')}
                      </button>
                    </fieldset>
                  ))}
                  <button
                    type="button"
                    className={control}
                    disabled={(form.parameters.nativeOrigins?.length ?? 0) >= 500}
                    onClick={() =>
                      params({
                        nativeOrigins: [
                          ...(form.parameters.nativeOrigins ?? []),
                          {
                            memberId: '',
                            observedAt: new Date().toISOString(),
                            evidenceRef: '',
                            evidenceSha256: '',
                          },
                        ],
                      })
                    }
                  >
                    {t('tagRules.addNative')}
                  </button>
                </>
              )}
              <button className={control} disabled={busy} type="submit">
                {t('tagRules.saveDraft')}
              </button>
              <button className={control} type="button" onClick={() => setForm(null)}>
                {t('tags.cancel')}
              </button>
            </form>
          )}
          {audit && (
            <div aria-label={t('tagRules.audit')}>
              <h3>{t('tagRules.audit')}</h3>
              {history.isError && <p role="alert">{t('tagRules.failed')}</p>}
              {history.data?.data.map((r) => (
                <button
                  key={r.id}
                  className={`${control} block`}
                  disabled={busy}
                  onClick={() =>
                    void perform(async () =>
                      setReport(await api.get<MemberTagRunDto>(`/member-tag-rules/runs/${r.id}`)),
                    )
                  }
                >
                  {new Date(r.cutoffInstant).toLocaleString()} · {t(`tagRules.${r.mode}`)} ·{' '}
                  {t(`tagRules.${r.status}`)}
                </button>
              ))}
              {more(auditPage, history.data?.total ?? 0, setAuditPage)}
            </div>
          )}
          {report && (
            <div aria-label={t('tagRules.report')} className="border p-3 break-words">
              <h3>{t('tagRules.report')}</h3>
              <p>
                {t(`tagRules.${report.status}`)} ·{' '}
                {t('tagRules.computed', { date: new Date(report.cutoffInstant).toLocaleString() })}
              </p>
              <p>
                {t('tagRules.watermark')}: {report.sourceWatermark ?? '—'}
              </p>
              <p>{t('tagRules.counts', report.counts)}</p>
              {report.errorCode && <p role="alert">{t('tagRules.failed')}</p>}
              <ul>
                {report.samples?.map((s) => (
                  <li key={s.memberId}>
                    {s.displayName} · {t(`tagRules.${s.classification}`)}
                  </li>
                ))}
              </ul>
              {report.nextCursor && (
                <button
                  className={control}
                  disabled={busy}
                  onClick={() =>
                    void perform(async () =>
                      setReport(
                        await api.get<MemberTagRunDto>(
                          `/member-tag-rules/runs/${report.id}?cursor=${encodeURIComponent(report.nextCursor!)}`,
                        ),
                      ),
                    )
                  }
                >
                  {t('tagRules.nextSamples')}
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
